import { getAuthToken } from "../../../auth/authClient";

const DEFAULT_TIMEOUT_MS = 30000;

const getStoreBaseUrl = () => {
  const envUrl = process.env.REACT_APP_MORFEUSZ_4E_API_URL;
  const cleanEnvUrl = String(envUrl || "").trim().replace(/\/$/, "");
  if (cleanEnvUrl) return cleanEnvUrl;

  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/wp-json/morfeusz/v1`;
  }

  return "";
};

const getMorfeuszNonce = () => {
  if (typeof window === "undefined") return "";
  try {
    if (typeof window.morfeuszNonce === "function") return String(window.morfeuszNonce() || "");
    const field = document.getElementById("morfeusz_nonce");
    return String(field?.value || field?.getAttribute?.("value") || "");
  } catch (_) {
    return "";
  }
};

const withTimeout = async (promise, timeoutMs = DEFAULT_TIMEOUT_MS) => {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error("request_timeout")), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const requestHeaders = (extra = {}) => {
  const token = getAuthToken();
  const nonce = getMorfeuszNonce();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(nonce ? { "X-Morfeusz-Nonce": nonce } : {}),
    ...extra,
  };
};

const normalizeFetchError = (error) => ({
  ok: false,
  code: error?.message === "request_timeout" ? "request_timeout" : "network_error",
  message: error?.message === "request_timeout"
    ? "Sklep nie odpowiedział na czas. Spróbuj za chwilę."
    : "Nie udało się połączyć ze sklepem. Spróbuj za chwilę.",
});

const normalizeBackendError = async (response) => {
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.ok) {
    return {
      ok: false,
      code: data?.code || "backend_error",
      message: data?.message || "Sklep zwrócił błąd.",
      status: response.status,
      data,
    };
  }
  return data;
};

const getJson = async (path, { auth = false } = {}) => {
  const baseUrl = getStoreBaseUrl();
  if (!baseUrl) {
    return {
      ok: false,
      code: "not_configured",
      message: "Sklep nie jest jeszcze skonfigurowany.",
    };
  }

  try {
    const response = await withTimeout(fetch(`${baseUrl}${path}`, {
      method: "GET",
      credentials: "include",
      headers: auth ? requestHeaders() : requestHeaders(),
    }));

    return await normalizeBackendError(response);
  } catch (error) {
    return normalizeFetchError(error);
  }
};

const postJson = async (path, body) => {
  const baseUrl = getStoreBaseUrl();
  if (!baseUrl) {
    return {
      ok: false,
      code: "not_configured",
      message: "Sklep nie jest jeszcze skonfigurowany.",
    };
  }

  try {
    const response = await withTimeout(fetch(`${baseUrl}${path}`, {
      method: "POST",
      credentials: "include",
      headers: requestHeaders({
        "Content-Type": "application/json",
      }),
      body: JSON.stringify(body || {}),
    }));

    return await normalizeBackendError(response);
  } catch (error) {
    return normalizeFetchError(error);
  }
};

export const storeClient = Object.freeze({
  isConfigured: () => Boolean(getStoreBaseUrl()),
  offers: () => getJson("/store/offers"),
  entitlements: () => getJson("/store/entitlements", { auth: true }),
  checkout: (offerSlug) => postJson("/store/checkout", { offer_slug: offerSlug }),
  session: (sessionId) => getJson(`/store/session?session_id=${encodeURIComponent(sessionId || "")}`, { auth: true }),
});
